import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import {
  VngButtonComponent,
  VngDialogActionsComponent,
  VngDialogRef,
  VngDialogTitleDirective,
  VngIconComponent,
  VngTextareaComponent,
} from "@vengreso/angular-components";

import { DialogCloseButtonComponent } from "src/app/components/dialogs/dialog-close-button/dialog-close-button.component";
import { SUBSCRIPTION_TAB } from "src/app/vengreso/settings/settings.config";

/** What {@link DeleteAccountDialogComponent} emits when the user confirms. */
export interface DeleteAccountDialogResult {
  reason: string;
}

/**
 * The account-deletion confirmation.
 *
 * Everything the decision needs lives here — the consequence list, the
 * "there is no undo" notice, the cancel-your-plan-instead escape hatch and the
 * required `reason` — so the Profile tab's danger zone stays a single button
 * and the user reads the warnings at the moment they act, not before.
 *
 * Closes with `{ reason }` on confirm and `undefined` on cancel/X/Escape.
 */
@Component({
  selector: "app-delete-account-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    VngButtonComponent,
    VngIconComponent,
    VngTextareaComponent,
    VngDialogTitleDirective,
    VngDialogActionsComponent,
    DialogCloseButtonComponent,
  ],
  templateUrl: "./delete-account-dialog.component.html",
  styleUrls: ["./delete-account-dialog.component.scss"],
})
export class DeleteAccountDialogComponent {
  form: FormGroup;

  /**
   * Spelled out so "everything" is not left to the imagination. Order =
   * roughly what a user would miss most.
   */
  readonly consequences = [
    "All of your FlyCuts, including their version history",
    "All of your FlyPlates and custom categories",
    "Your AI personas and any content generated with them",
    "Your usage statistics, FlyShare referrals and rewards",
    "Your extension settings, and the extension's access to this account",
    "Your active subscription — it is cancelled, and remaining time is not refunded",
  ];

  constructor(
    public dialogRef: VngDialogRef<DeleteAccountDialogComponent, DeleteAccountDialogResult>,
    private readonly fb: FormBuilder,
    private readonly router: Router
  ) {
    // `reason` is what the API persists to account_deletion_logs. The backend
    // types it `nullable|string|max:1000`; we require it (and a minimum of 10
    // characters) so the log is actually worth reading.
    this.form = this.fb.group({
      reason: [null, [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]],
    });
  }

  /** The non-destructive alternative: keep the account, drop the plan. */
  goToSubscription(): void {
    this.dialogRef.close();
    this.router.navigate(["/settings"], { queryParams: { tab: SUBSCRIPTION_TAB } });
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({ reason: (this.form.value.reason || "").trim() });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
